import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { InventoryPackSummary } from "../../../../../../models/inventory/InventoryAdmin";

export interface EditPackPopupSubmitPayload {
  label: string;
  plannedQty: number;
  description?: string;
}

interface EditPackPopupProps {
  open: boolean;
  pack: InventoryPackSummary | null;
  onClose: () => void;
  onSubmit: (payload: EditPackPopupSubmitPayload) => void;
}

export default function EditPackPopup({
  open,
  pack,
  onClose,
  onSubmit,
}: EditPackPopupProps) {
  const theme = useTheme();
  const [label, setLabel] = useState("");
  const [plannedQty, setPlannedQty] = useState("0");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open || !pack) return;
    setLabel(pack.label || "");
    setPlannedQty(String(pack.plannedQty ?? 0));
    setDescription(pack.description || "");
  }, [open, pack]);

  const parsedPlannedQty = Number(plannedQty);
  const isValid =
    label.trim().length > 0 &&
    Number.isFinite(parsedPlannedQty) &&
    Number.isInteger(parsedPlannedQty) &&
    parsedPlannedQty >= 0;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      label: label.trim(),
      plannedQty: parsedPlannedQty,
      description: description.trim() || undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Edit Pack
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Update pack label, planned quantity, and description
            </CustomTypography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  PACK LABEL
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    width="100%"
                    placeholder="Enter pack label"
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  PLANNED QTY
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="number"
                    value={plannedQty}
                    onChange={(e) => setPlannedQty(e.target.value)}
                    width="100%"
                    placeholder="Enter planned quantity"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  DESCRIPTION
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  Optional notes for this pack configuration
                </CustomTypography>
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  placeholder="Enter description"
                  multiline
                  minRows={3}
                  sx={{
                    mt: 1,
                    "& .MuiInputBase-input": { fontSize: "12px" },
                  }}
                />
              </Paper>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton label="Cancel" onClick={onClose} variant="grayscale" />
              <AppButton
                label="Save"
                onClick={handleSubmit}
                variant="primary"
                disabled={!isValid}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
